import { forwardRef, useEffect, useRef, useState } from 'react'
import { useComposedRefs } from '../../../../../utils/customHooks'
import PropTypes, { oneOf } from 'prop-types'
import { RadioContext } from './radio-context'
import { StyledRadioEmphasis, StyledRadioItem } from '../styled-components'
import { radioSizeOptions, radioVariantOptions } from '../constants'

export const radioVariantComponentOptions = {
  [radioVariantOptions.STANDARD]: StyledRadioItem,
  [radioVariantOptions.EMPHASIS]: StyledRadioEmphasis
}

const RadioRoot = forwardRef(
  (
    {
      name,
      size,
      variant,
      checked,
      onCheck,
      required,
      disabled,
      loading = false,
      ...props
    },
    forwardedRef
  ) => {
    const [button, setButton] = useState(null)
    const composedRefs = useComposedRefs(forwardedRef, (node) =>
      setButton(node)
    )

    const isFormControl = button ? Boolean(button.closest('form')) : true

    const getState = (checked) => {
      return checked ? 'checked' : 'unchecked'
    }

    const Component = radioVariantComponentOptions[variant] || radioVariantOptions.STANDARD

    return (
      <RadioContext.Provider
        value={{ size, variant, checked, disabled, getState }}
      >
        <Component
          size={size}
          variant={variant}
          type="button"
          role="radio"
          aria-checked={checked}
          data-state={getState(checked)}
          data-disabled={disabled ? '' : undefined}
          disabled={disabled}
          value={checked}
          {...props}
          ref={composedRefs}
          onClick={(event) => {
            // radios cannot be unchecked so we only communicate a checked state
            if (!checked) onCheck?.()
          }}
        />
        {isFormControl && (
          <BubbleInput
            control={button}
            name={name}
            value={checked}
            checked={checked}
            required={required}
            disabled={disabled}
            // We transform because the input is absolutely positioned but we have
            // rendered it **after** the button. This pulls it back to sit on top
            // of the button.
            style={{ transform: 'translateX(-100%)' }}
          />
        )}
      </RadioContext.Provider>
    )
  }
)

const BubbleInput = (props) => {
  const { checked, ...inputProps } = props
  const ref = useRef(null)
  const prevChecked = useRef(checked)

  useEffect(() => {
    const input = ref.current
    if (prevChecked.current !== checked) {
      input.checked = checked
      prevChecked.current = checked
    }
  }, [checked])

  return (
    <input
      type="checkbox"
      aria-hidden
      defaultChecked={checked}
      {...inputProps}
      tabIndex={-1}
      ref={ref}
      style={{
        ...props.style,
        position: 'absolute',
        pointerEvents: 'none',
        opacity: 0,
        margin: 0
      }}
    />
  )
}

RadioRoot.propTypes = {
  name: PropTypes.string,
  size: oneOf(Object.values(radioSizeOptions)),
  variant: oneOf(Object.values(radioVariantOptions)),
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onCheckedChange: PropTypes.func
}

RadioRoot.displayName = 'RadioRoot'
export default RadioRoot
